import { useEffect } from "react";

const PROD_DOMAIN = "https://southamptonportparkingservices.co.uk";

interface SeoOptions {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO_LD_ID = "page-jsonld";

export const useSeo = ({ title, description, canonical, keywords, jsonLd }: SeoOptions) => {
  useEffect(() => {
    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }

    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const url = canonical || `${PROD_DOMAIN}${path}`;
    upsertCanonical(url);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);

    // Inject page-specific JSON-LD (cleaned up on unmount)
    let script: HTMLScriptElement | null = null;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = SEO_LD_ID;
      script.text = JSON.stringify(jsonLd);
      // Remove any previous page LD
      const prev = document.getElementById(SEO_LD_ID);
      if (prev) prev.remove();
      document.head.appendChild(script);
    }

    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, canonical, keywords, JSON.stringify(jsonLd ?? null)]);
};
